import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp, 
  orderBy 
} from 'firebase/firestore';

/**
 * Checks if a user is enrolled in a course.
 * Replace this implementation with the actual document structure path in your DB.
 */
export const checkCourseEnrollment = async (userId, courseId) => {
  try {
    // Check in 'enrollments' collection
    const enrQ = query(
      collection(db, 'enrollments'), 
      where('userId', '==', userId), 
      where('courseId', '==', courseId)
    );
    const enrSnap = await getDocs(enrQ);
    if (!enrSnap.empty) return true;

    // Check in 'registrations' collection (if they are a customer)
    const regQ = query(
      collection(db, 'registrations'), 
      where('userId', '==', userId), 
      where('courseId', '==', courseId)
    );
    const regSnap = await getDocs(regQ);
    if (!regSnap.empty && regSnap.docs.some(d => d.data().status === 'customer')) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking course enrollment:", error);
    return false;
  }
};

/**
 * Checks if a user has completed a lesson.
 * Replace this implementation with the actual structure in your DB.
 */
export const checkLessonCompletion = async (userId, lessonId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) return false;
    
    const userData = userDoc.data();
    // Assuming lesson progress is stored in an object: { [lessonId]: true/false }
    return userData?.lessonProgress && userData.lessonProgress[lessonId] === true;
  } catch (error) {
    console.error("Error checking lesson completion:", error);
    return false;
  }
};

/**
 * Submits or updates a review.
 */
export const submitReview = async (user, targetType, targetId, rating, reviewText) => {
  try {
    if (!user || (!user.uid && !user.id)) throw new Error("User not authenticated.");
    const userId = user.uid || user.id;

    // Validation
    if (targetType === 'course') {
      const isEnrolled = await checkCourseEnrollment(userId, targetId);
      if (!isEnrolled) {
        throw new Error("You must be enrolled in this course to leave a review.");
      }
    } else if (targetType === 'lesson') {
      const isCompleted = await checkLessonCompletion(userId, targetId);
      if (!isCompleted) {
        throw new Error("You must complete this lesson to leave a review.");
      }
    } else if (targetType === 'instructor') {
      // Any authenticated user can rate an instructor
    } else {
      throw new Error("Invalid target type. Must be 'course', 'lesson', or 'instructor'.");
    }

    if (rating < 1 || rating > 5) {
      throw new Error("Rating must be between 1 and 5.");
    }

    const reviewId = `${userId}_${targetId}`;
    const reviewRef = doc(db, 'reviews', reviewId);

    const reviewData = {
      userId,
      userName: user.displayName || user.name || "Anonymous User",
      userPhoto: user.photoURL || user.photo || "",
      targetType,
      targetId,
      rating,
      reviewText,
      updatedAt: serverTimestamp(),
    };

    const existingReview = await getDoc(reviewRef);
    if (!existingReview.exists()) {
      reviewData.createdAt = serverTimestamp();
    }

    await setDoc(reviewRef, reviewData, { merge: true });
    return true;
  } catch (error) {
    console.error("Error submitting review:", error);
    throw error;
  }
};

/**
 * Fetches all reviews for a specific course or lesson.
 */
export const fetchReviewsForTarget = async (targetId) => {
  try {
    const reviewsRef = collection(db, 'reviews');
    // Remove orderBy from the query to avoid Firebase index requirement
    const q = query(
      reviewsRef, 
      where("targetId", "==", targetId)
    );
    
    const snapshot = await getDocs(q);
    const reviews = [];
    snapshot.forEach(doc => {
      reviews.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort reviews locally by updatedAt descending
    reviews.sort((a, b) => {
      const timeA = a.updatedAt ? a.updatedAt.toMillis() : 0;
      const timeB = b.updatedAt ? b.updatedAt.toMillis() : 0;
      return timeB - timeA;
    });
    
    return reviews;
  } catch (error) {
    console.error("Error fetching reviews:", error);
    return [];
  }
};

/**
 * Calculates the overall rating stats for a target.
 */
export const calculateReviewStats = (reviews) => {
  if (!reviews || reviews.length === 0) {
    return {
      average: 0,
      total: 0,
      distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
    };
  }

  let totalScore = 0;
  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  
  reviews.forEach(review => {
    totalScore += review.rating;
    if (distribution[review.rating] !== undefined) {
      distribution[review.rating]++;
    }
  });

  const average = Number((totalScore / reviews.length).toFixed(1));
  
  return {
    average,
    total: reviews.length,
    distribution
  };
};

/**
 * Allows an instructor or admin to reply to a review.
 */
export const replyToReview = async (reviewId, replyText, instructor) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    await setDoc(reviewRef, {
      reply: {
        text: replyText,
        instructorName: instructor.name || "المدرب",
        instructorPhoto: instructor.photoURL || "",
        repliedAt: serverTimestamp()
      }
    }, { merge: true });
    return true;
  } catch (error) {
    console.error("Error replying to review:", error);
    throw error;
  }
};
